// Renders stored model-space strings without changing visible text, while
// making durable entity aliases and UUID citations inspectable and navigable.
import { Link } from '@tanstack/react-router';
import { Check, Copy, ExternalLink } from 'lucide-react';
import { Fragment, type ReactNode, useState } from 'react';

import { Button } from '@apps/web/components/ui/button';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@apps/web/components/ui/popover';
import { ENTITY_REFERENCE_PATTERN } from '@apps/web/lib/entity_references';
import {
    type AgentTraceEntityAliasRead,
    type Mode,
} from '@libraries/typescript/api_client';

type ModelSpaceContentProps = {
    readonly text: string;
    readonly entityAliases: Readonly<Record<string, AgentTraceEntityAliasRead>>;
    readonly organizationId: string;
    readonly mode: Mode;
};

type ModelSpaceEntity = {
    readonly entityType: string;
    readonly entityId: string;
    readonly label: string | null;
};

type ModelSpaceToken =
    | { readonly kind: 'literal'; readonly text: string }
    | {
          readonly kind: 'entity';
          readonly text: string;
          readonly entity: ModelSpaceEntity;
      };

const ENTITY_ALIAS_SOURCE =
    '(?:shiftgroup|caregiver|outreach|client|shift|obj)\\d{3,}';

// The alternatives are ordered from most structured to least structured so a
// wrapped token is consumed whole before its alias can match as bare text.
// Group numbers below are positional: alias-in-token is 1-2, the shared
// reference pattern's three groups are 3-5, and the bare alias is 6. Adding
// a group to ENTITY_REFERENCE_PATTERN shifts the last one.
const MODEL_SPACE_TOKEN_PATTERN = new RegExp(
    [
        `\\[\\[([A-Za-z_]+):(${ENTITY_ALIAS_SOURCE})\\]\\]`,
        ENTITY_REFERENCE_PATTERN.source,
        `\\b(${ENTITY_ALIAS_SOURCE})\\b`,
    ].join('|'),
    'g',
);

function tokenizeModelSpaceContent(
    text: string,
    entityAliases: Readonly<Record<string, AgentTraceEntityAliasRead>>,
): Array<ModelSpaceToken> {
    const aliasesByEntityId = new Map(
        Object.values(entityAliases).map((alias) => [
            alias.entity_id.toLowerCase(),
            alias,
        ]),
    );
    const tokens: Array<ModelSpaceToken> = [];
    let cursor = 0;

    for (const match of text.matchAll(MODEL_SPACE_TOKEN_PATTERN)) {
        const index = match.index;
        if (index > cursor) {
            tokens.push({ kind: 'literal', text: text.slice(cursor, index) });
        }

        const tokenText = match[0];
        const alias = match[2] ?? match[6];
        const aliasDetail = alias ? entityAliases[alias] : undefined;
        if (aliasDetail) {
            tokens.push({
                kind: 'entity',
                text: tokenText,
                entity: {
                    entityType: aliasDetail.entity_type,
                    entityId: aliasDetail.entity_id,
                    label: aliasDetail.label,
                },
            });
        } else if (match[4]) {
            const entityId = match[4];
            const matchingAlias = aliasesByEntityId.get(entityId.toLowerCase());
            const referenceEntityType = (match[3] ?? '').toLowerCase();
            tokens.push({
                kind: 'entity',
                text: tokenText,
                entity: {
                    // UUID citations call an outreach a "callout", while alias
                    // rows use the canonical entity type "outreach".
                    entityType:
                        matchingAlias?.entity_type ??
                        (referenceEntityType === 'callout'
                            ? 'outreach'
                            : referenceEntityType),
                    entityId,
                    label: matchingAlias?.label ?? null,
                },
            });
        } else {
            tokens.push({ kind: 'literal', text: tokenText });
        }
        cursor = index + tokenText.length;
    }

    if (cursor < text.length) {
        tokens.push({ kind: 'literal', text: text.slice(cursor) });
    }
    return tokens;
}

function EntityLink({
    entityType,
    entityId,
    organizationId,
    mode,
}: ModelSpaceEntity & {
    readonly organizationId: string;
    readonly mode: Mode;
}): ReactNode {
    const className =
        'text-primary inline-flex items-center gap-1 text-xs font-medium underline-offset-2 hover:underline';
    if (entityType === 'caregiver') {
        return (
            <Link
                to="/caregivers"
                search={{ caregiverId: entityId, organizationId, mode }}
                className={className}
            >
                Open caregiver
                <ExternalLink className="h-3 w-3" />
            </Link>
        );
    }
    if (entityType === 'client') {
        return (
            <Link
                to="/clients"
                search={{ clientId: entityId, organizationId, mode }}
                className={className}
            >
                Open client
                <ExternalLink className="h-3 w-3" />
            </Link>
        );
    }
    if (entityType === 'shift') {
        return (
            <Link
                to="/shifts/$shift-id"
                params={{ 'shift-id': entityId }}
                search={{ organizationId, mode }}
                className={className}
            >
                Open shift
                <ExternalLink className="h-3 w-3" />
            </Link>
        );
    }
    if (entityType === 'outreach') {
        return (
            <Link
                to="/outreaches/$callout-id"
                params={{ 'callout-id': entityId }}
                search={{ organizationId, mode }}
                className={className}
            >
                Open outreach
                <ExternalLink className="h-3 w-3" />
            </Link>
        );
    }
    return null;
}

function AnnotatedEntityToken({
    text,
    entity,
    organizationId,
    mode,
}: {
    readonly text: string;
    readonly entity: ModelSpaceEntity;
    readonly organizationId: string;
    readonly mode: Mode;
}) {
    const [copied, setCopied] = useState(false);

    async function handleCopy(): Promise<void> {
        await navigator.clipboard.writeText(entity.entityId);
        setCopied(true);
    }

    return (
        <span
            data-entity-annotation={entity.entityId}
            data-testid="entity-annotation"
        >
            <Popover>
                <PopoverTrigger asChild={true}>
                    <button
                        type="button"
                        className="font-inherit rounded-sm border-b border-dotted border-emerald-600 bg-emerald-50 px-0.5 text-inherit dark:border-emerald-400 dark:bg-emerald-950"
                    >
                        {text}
                    </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-80 space-y-3">
                    <div>
                        <div className="text-sm font-semibold">
                            {entity.label ?? 'No mint-time label'}
                        </div>
                        <div className="text-muted-foreground text-xs">
                            Mint-time label
                        </div>
                    </div>
                    <dl className="grid gap-2 text-xs">
                        <div>
                            <dt className="text-muted-foreground">
                                Entity type
                            </dt>
                            <dd className="font-mono">{entity.entityType}</dd>
                        </div>
                        <div>
                            <dt className="text-muted-foreground">UUID</dt>
                            <dd className="font-mono break-all">
                                {entity.entityId}
                            </dd>
                        </div>
                    </dl>
                    <div className="flex items-center justify-between gap-2">
                        <EntityLink
                            {...entity}
                            organizationId={organizationId}
                            mode={mode}
                        />
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            aria-label="Copy entity UUID"
                            onClick={() => {
                                void handleCopy();
                            }}
                        >
                            {copied ? (
                                <Check className="h-3.5 w-3.5" />
                            ) : (
                                <Copy className="h-3.5 w-3.5" />
                            )}
                            {copied ? 'Copied' : 'Copy UUID'}
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>
        </span>
    );
}

/**
 * Renders model-space text byte-verbatim with optional entity annotations.
 *
 * The tokenizer's reconstruction invariant is that concatenating every token's
 * `text` equals the input exactly, so annotations never change visible text.
 * Rendering only annotates recognized aliases and UUID citations; unknown
 * alias-shaped tokens remain literal.
 */
export function ModelSpaceContent({
    text,
    entityAliases,
    organizationId,
    mode,
}: ModelSpaceContentProps): ReactNode {
    const tokens = tokenizeModelSpaceContent(text, entityAliases);
    return (
        <span className="whitespace-pre-wrap" data-testid="model-space-content">
            {tokens.map((token, index) => (
                <Fragment key={index}>
                    {token.kind === 'entity' ? (
                        <AnnotatedEntityToken
                            text={token.text}
                            entity={token.entity}
                            organizationId={organizationId}
                            mode={mode}
                        />
                    ) : (
                        token.text
                    )}
                </Fragment>
            ))}
        </span>
    );
}
